<template>
  <div class="p-grid">
    <div class="p-col-12">
      <div class="card card-w-title">

        <h4>Support Case</h4>

        <ProgressSpinner
          v-if="loading"
          style="width:25px; height:25px;"
          strokeWidth="5"
          fill="#EEEEEE"
          animationDuration=".5s"
        />

        <div v-else>
          <div
            v-for="group in groups"
            :key="group.title"
            class="support-case-group"
          >
            <h5>{{ group.title }}</h5>

            <div class="p-fluid p-formgrid p-grid">
              <div
                v-for="row in group.rows"
                :key="row.key"
                :data-field="row.key"
                :class="row.wide ? 'p-field p-col-12' : 'p-field p-col-12 p-md-4'"
              >
                <label>{{ row.label }}</label>

                <!-- The screenshot is opened the way every other attachment in the app is:
                     the file name plus a download button on AttachmentService. -->
                <div v-if="row.kind === 'attachment'" class="p-inputgroup">
                  <span class="support-case-value">{{ row.value }}</span>
                  <Button
                    icon="pi pi-download"
                    class="p-button-primary"
                    title="Download the screenshot"
                    @click="downloadScreenshot"
                  />
                </div>

                <!-- epPayload is a whole request body; it wraps and scrolls in its own box
                     rather than widening the page. -->
                <pre v-else-if="row.kind === 'payload'" class="support-case-payload">{{ row.value }}</pre>

                <div v-else class="support-case-value">{{ row.value }}</div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
// W7 T7: the read-only Support Case detail page.
//
// SupportCaseTable's View button pointed at a route that was never registered, so this page is
// what the whole table has been missing. It reads GET v1/usersupportcase/{id}, which already
// exists, and writes nothing - support cases are edited from the form and have no delete.
import SupportCaseService from "@/service/SupportCaseService";
import AttachmentService from "@/service/AttachmentService";

// Both enums come back as their raw constants (UserSupport.Type / UserSupport.Status). An
// unknown one falls back to the constant rather than disappearing.
const TYPE_LABELS = {
  ERROR_MSG: "Error Message",
  SCREEN_ISSUE: "Screen Issue",
  PERMISSION_ISSUE: "Permission Issue",
  UNEXPECTED_BEHAVIOUR: "Unexpected Behaviour",
  OTHER: "Other"
};

const STATUS_LABELS = {
  NEW: "New",
  VERIFIED: "Verified",
  ASSIGNED: "Assigned",
  ONHOLD: "On Hold",
  RESOLVED: "Resolved",
  DONE: "Done"
};

// Every field on the entity is optional, so a row is dropped rather than printed empty -
// "null" must never reach the screen.
function present(rows) {
  return rows.filter(
    row => row.value !== null && row.value !== undefined && row.value !== ""
  );
}

function userName(user) {
  if (!user) return null;
  if (user.fullName) return user.fullName;
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
  return name || user.email || null;
}

export default {
  created() {
    this.getSupportCase();
  },
  data: () => ({
    supportCaseService: new SupportCaseService(),
    attachmentService: new AttachmentService(),
    supportCase: {},
    loading: false
  }),
  computed: {
    groups() {
      return [
        { title: "The Case", rows: this.caseRows },
        { title: "What Was Reported", rows: this.reportedRows },
        { title: "Diagnostics", rows: this.diagnosticRows }
      ];
    },
    caseRows() {
      const supportCase = this.supportCase || {};
      return present([
        { key: "caseNo", label: "Case #", value: supportCase.caseNo },
        { key: "type", label: "Type", value: this.typeLabel },
        { key: "status", label: "Status", value: this.statusLabel },
        { key: "dateAdded", label: "Date & Time", value: supportCase.dateAdded },
        { key: "createdBy", label: "Raised By", value: userName(supportCase.createdBy) },
        // Never dropped: "not active" is a fact about the case, not a missing value.
        { key: "active", label: "Active?", value: supportCase.active ? "Yes" : "No" }
      ]);
    },
    reportedRows() {
      const supportCase = this.supportCase || {};
      return present([
        { key: "description", label: "Description", value: supportCase.description, wide: true },
        { key: "details", label: "Details", value: supportCase.details, wide: true },
        { key: "resolution", label: "Resolution", value: supportCase.resolution, wide: true },
        {
          key: "screenshot",
          label: "Screenshot",
          value: supportCase.screenshot ? supportCase.screenshot.fileName : null,
          kind: "attachment"
        }
      ]);
    },
    diagnosticRows() {
      const supportCase = this.supportCase || {};
      return present([
        { key: "lastEP", label: "Last Endpoint", value: supportCase.lastEP },
        { key: "epDateTime", label: "Endpoint Date & Time", value: supportCase.epDateTime },
        { key: "errorCode", label: "Error Code", value: supportCase.errorCode },
        { key: "errorEP", label: "Failing Endpoint", value: supportCase.errorEP },
        { key: "loggedInUser", label: "Logged In User", value: userName(supportCase.loggedInUser) },
        { key: "dateUpdated", label: "Last Updated", value: supportCase.dateUpdated },
        { key: "updatedBy", label: "Updated By", value: userName(supportCase.updatedBy) },
        { key: "epPayload", label: "Endpoint Payload", value: supportCase.epPayload, kind: "payload", wide: true }
      ]);
    },
    typeLabel() {
      const type = this.supportCase ? this.supportCase.type : null;
      return type ? TYPE_LABELS[type] || type : null;
    },
    statusLabel() {
      const status = this.supportCase ? this.supportCase.status : null;
      return status ? STATUS_LABELS[status] || status : null;
    }
  },
  watch: {
    "$route.params.caseid"() {
      this.getSupportCase();
    }
  },
  methods: {
    getSupportCase() {
      this.loading = true;

      this.supportCaseService
        .getSupportCase(this.$route.params.caseid)
        .then(data => {
          this.loading = false;
          this.supportCase = data || {};
        })
        .catch(err => {
          this.loading = false;

          this.$store.dispatch("addErrorMessage", {
            severity: "error",
            content: 'Some data was not loaded from "usersupportcase/{id}"'
          });
        });
    },
    downloadScreenshot() {
      if (this.supportCase && this.supportCase.screenshot) {
        this.attachmentService.fileDownloader(this.supportCase.screenshot.fileName);
      }
    }
  }
};
</script>

<style scoped>
.support-case-group {
  margin-bottom: 1.5rem;
}

.support-case-value {
  padding: 0.25rem 0;
  word-break: break-word;
}

/* A whole request body: it wraps and scrolls here instead of widening the page. */
.support-case-payload {
  margin: 0;
  padding: 0.5rem;
  max-height: 14rem;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 3px;
  font-size: 0.85rem;
}

/* Alignment (06/10): index.html also loads Bootstrap 4, whose padding utilities p-md-2/3/4
   (with !important) share their names with PrimeFlex's responsive columns, so these columns got
   7-21px of extra padding and sat lower / further in than the full-width fields. Put back the
   PrimeFlex form-grid padding (none above or below, 0.5rem each side) for this screen only. */
.support-case-group .p-formgrid.p-grid > .p-field {
  padding: 0 0.5rem !important;
}
</style>
